import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Input,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core'
import { AbstractControl, FormControl, FormGroup, Validators } from '@angular/forms'
import {
  AuthFacade,
  IdentityTypeEnum,
  VerifyExtras,
  SendVerificationPayload,
  VerificationPayload,
  VerificationTypeEnum,
} from 'libs/gb-front/auth/data-access/src'
import * as AuthActions from 'libs/gb-front/auth/data-access/src/lib/+state/auth.actions'
import { MatInput } from '@angular/material/input'
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy'
import { GbPopperService } from 'libs/gb-front/item/feature/src/lib/gb-popper/gb-popper.service'
import { AuctionFacade } from 'libs/gb-front/auction/data-access/src'
import { StringUtils } from 'libs/shared/utils/helpers/src'
import { GbVerifyIdentityService } from 'libs/gb-front/auth/util/src/lib/gb-verify-identity/gb-verify-identity.service'
import { distinctUntilChanged, Subscription } from 'rxjs'
import { GbCustomValidators } from 'libs/shared/utils/helpers/src/lib/form-validators/custom-form.validator'

@UntilDestroy()
@Component({
  selector: 'gb-verify-form',
  templateUrl: './gb-verify-form.component.html',
  styleUrls: ['./gb-verify-form.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class GbVerifyFormComponent implements OnInit, AfterViewInit, OnDestroy {
  @Input() dialog = false
  @Input() hideSubmitButton = false
  @Input() verificationDescription?: string
  @Input() verifyExtras?: VerifyExtras

  @ViewChild('identityInput') identityInput?: MatInput
  @ViewChild('vcInput') vcInput!: MatInput

  identityValue!: string | null
  identityType: IdentityTypeEnum = IdentityTypeEnum.Email
  isBothEmailMobileAllowed = false
  identityControlSubscription?: Subscription

  auction$ = this.auctionFacade.auction$

  verificationCode: FormControl = new FormControl('', [Validators.required, Validators.minLength(6)])

  form: FormGroup = new FormGroup({
    identity: new FormControl('', [Validators.required]),
    verification_code: this.verificationCode,
  })

  get isEmailType(): boolean {
    return this.identityType === IdentityTypeEnum.Email
  }

  get isMobileType(): boolean {
    return this.identityType === IdentityTypeEnum.Mobile
  }

  get identityControl(): AbstractControl {
    return this.form.get('identity')!
  }

  get identityTypeFromValue(): IdentityTypeEnum | undefined {
    if (this.identityValue) {
      if (StringUtils.isEmail(this.identityValue)) {
        return IdentityTypeEnum.Email
      } else if (StringUtils.isPhoneNumber(this.identityValue)) {
        return IdentityTypeEnum.Mobile
      }
    }
    return undefined
  }

  get verificationValue(): string {
    return this.form.get('verification_code')!.value
  }

  constructor(
    private auctionFacade: AuctionFacade,
    private authFacade: AuthFacade,
    private gbVerifyIdentity: GbVerifyIdentityService,
    private cdr: ChangeDetectorRef,
    private gbPopperService: GbPopperService,
  ) {}

  ngOnInit(): void {
    this.gbPopperService.disablePoppers()

    this.auctionFacade.identityType$.pipe(untilDestroyed(this), distinctUntilChanged()).subscribe((identityType) => {
      this.gbVerifyIdentity.identity
        ? this.setupOnRegister(identityType) // verifying after register step
        : this.setupOnReturn(identityType) // returning later to verify

      this.cdr.detectChanges()
    })
  }

  ngOnDestroy(): void {
    this.gbPopperService.enablePoppers()
    this.identityControlSubscription?.unsubscribe()
  }

  ngAfterViewInit(): void {
    this.authFacade.authVerifyError$.pipe(untilDestroyed(this)).subscribe((error) => {
      if (error) {
        this.setVerifyError(error)
        this.authFacade.dispatch(AuthActions.clearVerifyError())
      }
    })
  }

  setupOnRegister(identityType: IdentityTypeEnum): void {
    this.setIdentityValue(this.gbVerifyIdentity.identity)
    if (this.identityTypeFromValue) {
      this.identityType = this.identityTypeFromValue
    } else {
      this.identityType = identityType === IdentityTypeEnum.Both ? IdentityTypeEnum.Email : identityType
    }
  }

  setupOnReturn(identityType: IdentityTypeEnum): void {
    this.isBothEmailMobileAllowed = identityType === IdentityTypeEnum.Both
    this.setIdentityType(identityType)
    if (this.isBothEmailMobileAllowed) {
      this.watchIdentityChanges()
    }
  }

  setIdentityValue(value: string): void {
    this.form.controls['identity'].setValue(value)
    this.identityValue = value
  }

  watchIdentityChanges(): void {
    this.identityControlSubscription = this.identityControl.valueChanges.subscribe((value) =>
      this.setIdentityTypeOnValueChanges(value),
    )
  }

  setIdentityTypeOnValueChanges(value: string): void {
    // allow numbers, hyphens and plus sign
    const pattern = /^(?=.*[0-9])[- +()0-9]+$/

    if (value.length > 5 && pattern.test(value) && this.isEmailType) {
      this.setIdentityType(IdentityTypeEnum.Mobile)
    } else if (!pattern.test(value) && this.isMobileType) {
      this.setIdentityType(IdentityTypeEnum.Email)
      this.cdr.detectChanges()
      this.identityInput?.focus()
    }
  }

  setIdentityType(identityType: IdentityTypeEnum): void {
    this.identityType = identityType === IdentityTypeEnum.Both ? IdentityTypeEnum.Email : identityType
    this.setIdentityValidator()
  }
  /**
   * Set error on form control on invalid verification code
   */
  setVerifyError(error: string): void {
    this.verificationCode.setErrors({ verify: error })
    this.verificationCode.markAsTouched()
    this.vcInput.focus()
    this.cdr.detectChanges()
  }

  setIdentityValidator(): void {
    this.isMobileType ? this.setPhoneValidator() : this.setEmailValidator()
  }

  setEmailValidator(): void {
    this.identityControl.removeValidators(GbCustomValidators.mobileNumberValidator)
    this.identityControl.addValidators(Validators.email)
  }

  setPhoneValidator(): void {
    this.identityControl.removeValidators(Validators.email)
    this.identityControl.addValidators(GbCustomValidators.mobileNumberValidator)
  }

  getVerificationDescription(): string {
    if (this.verificationDescription) {
      return this.verificationDescription
    }
    if (this.isBothEmailMobileAllowed) {
      return 'AUTH.VERIFY.DESCRIPTION'
    }
    return this.isMobileType ? 'AUTH.VERIFY.PHONE_DESCRIPTION' : 'AUTH.VERIFY.EMAIL_DESCRIPTION'
  }

  getIdentityInputLabel(): string {
    if (this.isBothEmailMobileAllowed) {
      return 'AUTH.VERIFY.IDENTITY.BOTH'
    }
    return this.isMobileType ? 'AUTH.VERIFY.IDENTITY.PHONE' : 'AUTH.VERIFY.IDENTITY.EMAIL'
  }

  getIdentityType(): IdentityTypeEnum {
    return this.isBothEmailMobileAllowed ? IdentityTypeEnum.Both : this.identityType
  }

  setIdentity(value: string): void {
    this.form.controls['identity'].setValue(value)
    this.identityValue = value
  }

  resendVerification(): void {
    const payload: SendVerificationPayload = {
      verification_type: this.isMobileType ? VerificationTypeEnum.PhoneNumber : VerificationTypeEnum.Email,
      ...(this.isMobileType ? { phone_number: this.identityValue } : { email: this.identityValue }),
      ...this.verifyExtras,
    }

    this.authFacade.dispatch(
      AuthActions.sendVerification({
        uuid: this.gbVerifyIdentity.uuid,
        payload,
      }),
    )
  }

  verify(): void {
    if (this.hideSubmitButton) return
    const verificationPayload: VerificationPayload = this.form.value
    this.authFacade.dispatch(
      AuthActions.verify({
        payload: verificationPayload,
        dialog: this.dialog,
      }),
    )
  }

  shouldShowEmailVerificationHelp(): boolean {
    const identityType = this.getIdentityType()
    return identityType === IdentityTypeEnum.Email || identityType === IdentityTypeEnum.Both
  }

  shouldShowPhoneVerificationHelp(): boolean {
    const identityType = this.getIdentityType()
    return identityType === IdentityTypeEnum.Mobile || identityType === IdentityTypeEnum.Both
  }
}
